// Copyright (c) Ensho Health, 2021 to present

import api from '../api';
import actions from './index.js';
import { HELP_CODES } from '../../constants';
import { getKey } from '../../helper/encryption/index-db-utils.js';
import { setNotification, NOTIFICATION_TYPES } from './notification.action.js';

const PREFIX = 'encryption';
export const ACTION_TYPES = {
  KEY_STORED: `${PREFIX}-KEY_STORED`,
  DECRYPT_PROGRESS: `${PREFIX}-DECRYPT_PROGRESS`,
  ERROR: `${PREFIX}-ERROR`,
  LOADING: `${PREFIX}-LOADING`,
  STOP_LOADING: `${PREFIX}-STOP_LOADING`,
  FORGOTTEN_SITE_PASSWORD: `${PREFIX}-FORGOTTEN_SITE_PASSWORD`
};

/**
 * Check if the clinic's private key has been stored locally. If so,
 * the state manager is updated so that we don't ask the user to enter
 * the clinic key again.
 * @returns a promise
 */
export function init() {
  return async (dispatch, getState) => {
    try {
      const privateKey = await getKey();
      if (privateKey) {
        const state = getState();
        state.encryption.rsa.setPrivateKey(privateKey);
        dispatch({ type: ACTION_TYPES.KEY_STORED });
      }
    } catch (error) {
      console.error(
        'state-manager.action.encryption.action.js: ' + error.message
      );
      throw new Error('error while initializing the private key');
    }
  };
}

/**
 * Given a clear text user input, assess if it's correct by generating the
 * related public key and comparing with the one stored on the backend.
 * Also, update the decrypt progess state so that components know how advanced
 * the process is. It goes fast to 99% and remain stuck a long time there,
 * so it may not be as useful as it sounds.
 * @param {string} key the user's input
 * @returns a promise
 */
export function validateClinicKey(key) {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      if (state?.user?.data?.hasCodAccess) {
        dispatch({ type: ACTION_TYPES.LOADING });
        // fetching the clinic public keys
        const clinicId = state?.user?.data?.clinic?.clinicId;
        dispatch({ type: ACTION_TYPES.LOADING });
        const keys = await api.encryption.fetchPublicClinicKeys(clinicId);

        // generating the pub/private rsa keys from the user input
        state.encryption.rsa.setSiteSalt(keys.site.siteSalt);
        state.encryption.rsa.setCryptoParameters(keys.site.cryptoParameters);

        function onProgress(progress) {
          dispatch({ type: ACTION_TYPES.DECRYPT_PROGRESS, payload: progress });
        }

        const { publicKey } = await state.encryption.rsa.generateRSAKeys(
          key,
          onProgress
        );

        // validating the user's input
        const sitePublicKeyCandidate = await publicKey.toPem();
        if (sitePublicKeyCandidate === keys.site.sitePubkey) {
          await state.encryption.rsa.savePrivateKey();
          // if PII data have already been fetched at this point,
          // we need to decrypt them
          await dispatch(actions.pii.fetchPii());
          dispatch({ type: ACTION_TYPES.KEY_STORED });
        } else {
          throw new Error('Wrong clinic key');
        }
      }
    } catch (error) {
      console.error('encryption.action.validateClinicKey: ' + error.message);
      // not displaying any message to the user here, they are already
      // alerted via the form
      await setNotification({
        type: NOTIFICATION_TYPES.ERROR,
        serverMessage: error.message,
        info: { location: 'encryption.action.validateClinicKey' }
      });
      dispatch({ type: ACTION_TYPES.ERROR, payload: error.message });
    }
  };
}

/**
 * Ask for some assistance. AN email will be sent to Ensho's assistance's team
 */
export const forgottenSiteKey = () => {
  return async (dispatch) => {
    try {
      const queryResponse = await api.encryption.userNeedsHelp(
        HELP_CODES.FORGOTTEN_SITE_KEY
      );
      if (queryResponse.ok) {
        await dispatch({
          type: ACTION_TYPES.FORGOTTEN_SITE_PASSWORD
        });
      } else {
        throw new Error(
          `Error with userNeedsHelp mutation: ${queryResponse.error}`
        );
      }
    } catch (error) {
      console.error(error.message);
      await setNotification({
        type: NOTIFICATION_TYPES.ERROR,
        serverMessage: error.message,
        display: 'Error while notifying our team for help. Please try again.',
        info: { location: 'encryption.action.forgottenSiteKey' }
      });
      dispatch({ type: ACTION_TYPES.ERROR, payload: error.message });
    }
  };
};

/***
 * Send the newly generated public key for this clinic to the backend
 */
export const setupClinic = (password) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: ACTION_TYPES.LOADING });
      const state = getState();
      const clinicId = state?.user?.data?.clinic?.clinicId;
      const keys = await api.encryption.fetchPublicClinicKeys(clinicId);
      if (keys.keyChangeEnabled) {
        // generating the pub/private rsa keys from the user input
        state.encryption.rsa.setSiteSalt(keys.randomSalt);
        state.encryption.rsa.setCryptoParameters(keys.site.cryptoParameters);
        const { publicKey } = await state.encryption.rsa.generateRSAKeys(
          password
        );
        const sitePublicKey = await publicKey.toPem();
        // sending the generated public key to the backend for it to be stored
        const response = await api.encryption.setSitePublicKey(
          clinicId,
          parseInt(keys.site.cryptoParameters.id, 10),
          sitePublicKey,
          keys.randomSalt
        );
        if (response.ok) {
          // completing setup in the backend
          const completeSetup = await api.clinic.clinicSetupComplete(clinicId);
          if (completeSetup.ok) {
            // saving the private key locally to decrypt pii
            await state.encryption.rsa.savePrivateKey();
            dispatch({ type: ACTION_TYPES.KEY_STORED });
            dispatch({ type: actions.user.ACTION_TYPES.COMPLETE_CLINIC_SETUP });
          } else {
            let errorMessage = completeSetup.error;
            if (!completeSetup.error) {
              errorMessage = 'error while completing the clinic setup';
            }
            throw new Error(errorMessage);
          }
        } else {
          throw new Error('something went wrong while setting the public key');
        }
      } else {
        throw new Error('The key for this clinic cannot be changed');
      }
    } catch (error) {
      console.error(error.message);
      const state = getState();
      const clinicId = state?.user?.data?.clinic?.clinicId;
      // not displaying any message to the user here, they are already
      // alerted via the form
      dispatch(
        setNotification({
          type: NOTIFICATION_TYPES.ERROR,
          serverMessage: error.message,
          info: {
            location: 'encryption.action.setupClinic',
            clinicId: clinicId
          }
        })
      );
      dispatch({ type: ACTION_TYPES.ERROR, payload: error.message });
    }
  };
};
